import type { Service } from '@/types'
import { QUOTE_PATH } from '@/constants/site'

const quoteHref = (slug: string): string => `${QUOTE_PATH}?service=${slug}&entry=service_page`

const priceClosing = (slug: string) => ({
    title: "Know the price before anyone picks up a tool.",
    body: "Send us a few details and a written price range lands on your phone, usually inside 30 minutes.",
    cta: { label: "Price this job", href: quoteHref(slug) }
})

export const services: Service[] = [
    {
        slug: 'emergency-repair',
        href: '/services/emergency-repair',
        formService: 'emergency',
        title: "Emergency repair",
        footerLabel: "Emergency repair",
        meta: "Under 3 h · on call until 10 PM",
        summary: "A snapped spring, a door jammed open, a car shut inside. Ring us now, or leave your number and a technician calls back.",
        priceLabel: "Call or text back",
        image: '/images/services/emergency-repair.jpg',
        imageAlt: "Technician adjusting a torsion spring above a garage door",
        kind: "Emergency",
        homeImage: '/images/home/service-emergency-dusk.png',
        homeImageAlt: "Liftline technician adjusting a garage door panel at dusk, with the lit house and driveway behind him",
        homeImagePosition: '36% 50%',
        detail: {
            eyebrow: "Emergency · On call until 10 PM",
            title: "Door won't close tonight? One call gets a technician moving.",
            intro: "Your call goes to a technician on duty, never a call center. Across the Riverton metro, most emergency visits arrive inside 3 hours.",
            facts: [
                { label: "After 7 PM", value: "+$75 flat" },
                { label: "Arrival", value: "Under 3 h" },
                { label: "Warranty", value: "2 years labor" }
            ],
            primaryCta: 'call',
            secondaryCta: { label: "Have a technician call me", href: '#callback' },
            sections: [
                {
                    type: 'steps',
                    title: "Until we get there",
                    items: [
                        "Keep children, pets and anyone curious well clear of the door.",
                        "Leave a door with a broken spring where it is. Lifting it by hand is how people get hurt.",
                        "If the door is stuck open and steady, a parked car underneath can stop it dropping. Never stand under it yourself.",
                        "Unplug the opener so the motor stops fighting the door."
                    ]
                },
                {
                    type: 'list',
                    title: "Call us straight away for",
                    items: [
                        "Broken spring",
                        "Door off track",
                        "Door stuck open overnight",
                        "Car trapped inside",
                        "Door hit by a vehicle"
                    ]
                },
                {
                    type: 'callout',
                    title: "The after-hours price, up front",
                    body: "Call-outs after 7 PM carry one flat $75 on top of the repair. We tell you before we drive over, so the invoice holds no surprises."
                }
            ],
            closing: {
                title: "Busy right now?",
                body: "Leave your ZIP code and number and the on-call technician rings you back.",
                cta: { label: "Have a technician call me", href: '#callback' }
            }
        }
    },
    {
        slug: 'broken-spring-repair',
        href: '/services/broken-spring-repair',
        formService: 'spring',
        title: "Spring replacement",
        footerLabel: "Broken springs",
        meta: "~45 min",
        summary: "Torsion and extension springs fitted as a matched pair, rated for 20,000 cycles and balanced to your door.",
        priceLabel: "From $225",
        image: '/images/services/spring-replacement.jpg',
        imageAlt: "Gloved hands fitting a new torsion spring",
        kind: "Repair",
        homeImage: '/images/home/service-torsion-spring.png',
        homeImageAlt: "Gloved hands tightening a torsion spring bracket with an impact driver",
        homeImagePosition: '56% 50%',
        detail: {
            eyebrow: "Service · Repair",
            title: "A broken spring, replaced and balanced in about 45 minutes.",
            intro: "We fit a matched pair of torsion springs rated for 20,000 cycles, balance the door to its weight and test it before we leave.",
            facts: [
                { label: "Typical price", value: "$225–$395" },
                { label: "Time", value: "~45 min" },
                { label: "Warranty", value: "Lifetime (parts)" }
            ],
            primaryCta: { label: "Price this job", href: quoteHref('broken-spring-repair') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'list',
                    title: "How a broken spring shows itself",
                    items: [
                        "A sharp bang from the garage, loud enough to hear indoors",
                        "The door lifts a few inches, then gives up",
                        "A gap in the coil on the bar above the door",
                        "Lift cables hanging slack"
                    ]
                },
                {
                    type: 'steps',
                    title: "How the visit runs",
                    items: [
                        "Lock the door in place and let off any tension left in the system",
                        "Measure the wire size, length and inside diameter",
                        "Fit a matched pair straight from the truck",
                        "Swap out cables that show fraying",
                        "Balance the door and set the opener's force",
                        "Run the door through 10 full cycles before we leave"
                    ]
                },
                {
                    type: 'callout',
                    title: "Why prices vary",
                    body: "The weight and size of the door, standard or high-cycle springs, and whether the cables or bearings need replacing as well."
                }
            ],
            closing: priceClosing('broken-spring-repair')
        }
    },
    {
        slug: 'openers-smart-garage',
        href: '/services/openers-smart-garage',
        formService: 'opener',
        title: "Openers & smart garage",
        homeTitle: "Opener repair & installation",
        footerLabel: "Openers & smart garage",
        meta: "~1–2 h",
        summary: "Chain, belt and wall-mount openers, set up with app control and battery backup.",
        priceLabel: "From $125",
        image: '/images/services/opener.jpg',
        imageAlt: "Belt-drive garage door opener on a garage ceiling",
        kind: "Openers",
        homeImage: '/images/home/service-smart-opener.png',
        homeImageAlt: "Ceiling-mounted smart opener with a built-in light above an open sectional garage door at sunset",
        homeImagePosition: '50% 50%',
        detail: {
            eyebrow: "Openers & smart garage",
            title: "A door you open from your phone, and barely hear.",
            intro: "Near-silent belt-drive and wall-mount openers with app control, battery backup and access for package deliveries. We repair every major brand too.",
            facts: [
                { label: "Typical price", value: "From $125" },
                { label: "Time", value: "~1–2 h" },
                { label: "Warranty", value: "2 years labor" }
            ],
            primaryCta: { label: "Price this job", href: quoteHref('openers-smart-garage') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'options',
                    title: "Pick your opener",
                    items: [
                        { name: "Chain drive", description: "Hard-wearing and easy on the budget, a little louder.", price: "From $450 installed" },
                        { name: "Belt drive + Wi-Fi", description: "Close to silent, with app control and battery backup.", price: "From $620 installed" },
                        { name: "Wall-mount (jackshaft)", description: "Clears the ceiling and runs quieter than anything else.", price: "From $890 installed" }
                    ]
                },
                {
                    type: 'list',
                    title: "Time to call us when",
                    items: [
                        "The remote or keypad has stopped responding",
                        "The motor hums but the door stays put",
                        "The opener grinds as it runs",
                        "You'd like app control or battery backup"
                    ]
                },
                {
                    type: 'steps',
                    title: "How the visit runs",
                    items: [
                        "Test the opener, safety sensors and remotes",
                        "Fix the gear, board or sensors, or price a replacement",
                        "Fit and pair the remotes, keypad and app",
                        "Set the force and travel limits, then run the safety test"
                    ]
                },
                {
                    type: 'callout',
                    title: "Opener repairs",
                    body: "Worn gears, dead remotes, keypad pairing, sensors and logic boards, starting at $125. If your opener's parts are no longer made, you'll know before we leave."
                }
            ],
            closing: priceClosing('openers-smart-garage')
        }
    },
    {
        slug: 'off-track-cable-repair',
        href: '/services/off-track-cable-repair',
        formService: 'offtrack',
        title: "Off-track & cable repair",
        footerLabel: "Off-track & cables",
        meta: "~1 h",
        summary: "Bent tracks, frayed cables and rollers that jumped out. We find out why it happened, then fix that too.",
        priceLabel: "From $175",
        image: '/images/services/off-track-cables.jpg',
        imageAlt: "Garage door track and rollers with a wrench on the bracket",
        kind: "Repair",
        homeImage: '/images/home/service-track-repair.png',
        homeImageAlt: "Technician kneeling to adjust the track and bottom bracket of a white sectional garage door",
        homeImagePosition: '62% 50%',
        detail: {
            eyebrow: "Service · Repair",
            title: "Back on its tracks, and staying there.",
            intro: "Bent tracks, frayed cables and rollers that have jumped out. We put the door back in line and deal with whatever knocked it out in the first place.",
            facts: [
                { label: "Typical price", value: "$175–$350" },
                { label: "Time", value: "~1 h" },
                { label: "Warranty", value: "2 years labor" }
            ],
            primaryCta: { label: "Price this job", href: quoteHref('off-track-cable-repair') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'list',
                    title: "How it shows itself",
                    items: [
                        "One side of the door hangs lower than the other",
                        "Rollers sitting outside the track",
                        "Lift cables frayed or loose",
                        "The door jerks or stalls halfway"
                    ]
                },
                {
                    type: 'steps',
                    title: "How the visit runs",
                    items: [
                        "Secure the door so it cannot drop",
                        "Seat the rollers and straighten or replace any bent track",
                        "Replace frayed cables",
                        "Balance the door and test it"
                    ]
                },
                {
                    type: 'callout',
                    title: "Stay clear until we arrive",
                    body: "Keep cars and people away from a crooked door and unplug the opener so it stops pulling. A door that has come loose can fall.",
                    tone: 'warning'
                }
            ],
            closing: priceClosing('off-track-cable-repair')
        }
    },
    {
        slug: 'panel-replacement',
        href: '/services/panel-replacement',
        formService: 'panel',
        title: "Panel replacement",
        footerLabel: "Panel replacement",
        meta: "~2 h",
        summary: "A car, a stray ball or a storm left a dent. We match the new section to your door's maker and color.",
        priceLabel: "From $250",
        image: '/images/services/panel-replacement.jpg',
        imageAlt: "Two technicians lifting a new door panel into place",
        kind: "Repair",
        homeImagePosition: '50% 40%',
        detail: {
            eyebrow: "Service · Repair",
            title: "One dented panel, not a whole new door.",
            intro: "Cars, stray balls and storms all leave their mark. We source a section that matches your door's maker and color, and swap only what's damaged.",
            facts: [
                { label: "Typical price", value: "$250–$800" },
                { label: "Time", value: "~2 h" },
                { label: "Warranty", value: "2 years labor" }
            ],
            primaryCta: { label: "Price this job", href: quoteHref('panel-replacement') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'list',
                    title: "When a panel needs replacing",
                    items: [
                        "A section is dented or cracked",
                        "The door no longer seals along the bottom",
                        "A panel bent after a knock"
                    ]
                },
                {
                    type: 'steps',
                    title: "How the visit runs",
                    items: [
                        "Read the door's model and color from its label",
                        "Order the matching section",
                        "Swap the damaged section and rehang the door",
                        "Balance the door and test it"
                    ]
                },
                {
                    type: 'callout',
                    title: "Why prices vary",
                    body: "The number of damaged sections, who made the door, and whether a matching panel is still produced. Send a photo of the label and we can price it exactly."
                }
            ],
            closing: priceClosing('panel-replacement')
        }
    },
    {
        slug: 'new-garage-doors',
        href: '/new-doors',
        formService: 'new-door',
        title: "New garage doors",
        footerLabel: "New doors",
        meta: "1 day",
        summary: "Steel, wood-look composite, carriage house and full-view glass, fitted in a single day with the old door hauled away.",
        priceLabel: "From $1,150",
        image: '/images/services/new-doors.jpg',
        imageAlt: "Walnut carriage-house garage door on a craftsman home",
        kind: "Installation",
        homeImage: '/images/home/service-new-door-install.png',
        homeImageAlt: "Technician fitting the track of a new walnut wood-look flush garage door",
        homeImagePosition: '68% 50%'
    },
    {
        slug: 'commercial-doors',
        href: '/services/commercial-doors',
        formService: 'commercial',
        title: "Commercial doors",
        footerLabel: "Commercial doors",
        meta: "On site",
        summary: "Rolling steel, sectional and high-speed doors, plus maintenance plans that keep a business moving.",
        priceLabel: "Quoted on site",
        image: '/images/services/commercial-doors.jpg',
        imageAlt: "Roll-up steel doors on a warehouse at dusk",
        kind: "Commercial",
        homeImagePosition: '62% 50%',
        detail: {
            eyebrow: "Commercial",
            title: "When the door stops, the business stops. We get it moving again.",
            intro: "Rolling steel, sectional and high-speed doors for warehouses, auto shops, storage facilities and shops. Same-day response when a door is holding up your work.",
            facts: [
                { label: "Pricing", value: "Quoted on site" },
                { label: "Response", value: "Same day" },
                { label: "Plans", value: "Maintenance" }
            ],
            primaryCta: { label: "Book a site visit", href: quoteHref('commercial-doors') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'list',
                    title: "Doors we look after",
                    items: [
                        "Rolling steel doors",
                        "Sectional doors",
                        "Dock levelers & seals",
                        "High-speed doors",
                        "Fire-door inspections"
                    ]
                },
                {
                    type: 'steps',
                    title: "How we work with you",
                    items: [
                        "A site visit and a written quote",
                        "Repair or replace the door, operator or seals",
                        "Quarterly maintenance, if you want it",
                        "A door log you can hand to your insurer"
                    ]
                },
                {
                    type: 'callout',
                    title: "Maintenance plans",
                    body: "Visits every quarter or twice a year, priority dispatch when something breaks, and a written door log for your insurer."
                }
            ],
            closing: {
                title: "A door holding up your day?",
                body: "Tell us what's wrong and we'll book a site visit, the same day when it can't wait.",
                cta: { label: "Book a site visit", href: quoteHref('commercial-doors') }
            }
        }
    },
    {
        slug: 'tune-up-maintenance',
        href: '/services/tune-up-maintenance',
        formService: 'tune-up',
        title: "Tune-up & maintenance",
        footerLabel: "Tune-up & maintenance",
        meta: "~45 min",
        summary: "A 21-point safety check, roller care, lubrication and a balance test.",
        priceLabel: "From $95",
        image: '/images/projects/millbrook-after.jpg',
        imageAlt: "Balanced torsion springs and fresh hardware above a white garage door",
        kind: "Maintenance",
        detail: {
            eyebrow: "Service · Maintenance",
            title: "A quieter door, and fewer surprises.",
            intro: "A 21-point safety check, new nylon rollers where they're needed, lubrication and a balance test. Small work now that saves a call-out later.",
            facts: [
                { label: "Typical price", value: "$95–$165" },
                { label: "Time", value: "~45 min" },
                { label: "Warranty", value: "2 years labor" }
            ],
            primaryCta: { label: "Price this job", href: quoteHref('tune-up-maintenance') },
            secondaryCta: 'call',
            sections: [
                {
                    type: 'list',
                    title: "Worth booking when",
                    items: [
                        "The door grinds or squeals",
                        "It shudders on the way up or down",
                        "It has been 2+ years since anyone checked it"
                    ]
                },
                {
                    type: 'steps',
                    title: "How the visit runs",
                    items: [
                        "21-point safety inspection",
                        "Lubricate the hinges, rollers and springs",
                        "Tighten every fixing",
                        "Test the balance and the opener's force"
                    ]
                },
                {
                    type: 'callout',
                    title: "Why prices vary",
                    body: "Whether any rollers or hinges need replacing, and the size of the door."
                }
            ],
            closing: priceClosing('tune-up-maintenance')
        }
    }
]

export const homeServices: Service[] = services.filter(service => service.slug !== 'tune-up-maintenance')

export const detailServices: Service[] = services.filter(service => service.detail)

export const getServiceBySlug = (slug: string): Service | undefined =>
    detailServices.find(service => service.slug === slug)
